/**
 * <product-info-card>
 * Custom element for sections/product-info-card.liquid. Two independent,
 * unrelated interactions live on the same element:
 *
 * 1) Portionsgrößen-Switch ("Pro 100 g" / "Pro Portion", [data-pic-toggle]):
 *    swaps the data-pic-view attribute on the root element, CSS
 *    (section-product-info-card.css) hides/shows the matching
 *    .pic-value--hundred / .pic-value--portion spans accordingly. Sits
 *    inside the "Nährwerte" tab panel, unaffected by the tab switching below.
 *
 * 2) Reiterleiste (Pill-Tabs, [data-pic-tab], added 2026-10-07 to replace
 *    the former <details>/<summary> accordion): clicking a tab shows its
 *    [role="tabpanel"] (matched via aria-controls) and hides all others via
 *    the `hidden` attribute, toggles aria-selected/tabindex, and supports
 *    Left/Right/Home/End arrow-key navigation between tabs (roving
 *    tabindex, same pattern as component-tabbed-products.js).
 *
 * Both run via plain attribute/class toggling, no page reload, no fetch.
 */
class ProductInfoCard extends HTMLElement {
  connectedCallback() {
    // 1) Portionsgrößen-Switch
    this.buttons = this.querySelectorAll('[data-pic-toggle]');
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.setView(btn.getAttribute('data-pic-toggle')));
    });

    // 2) Reiterleiste
    this.tabButtons = Array.from(this.querySelectorAll('[data-pic-tab]'));
    this.tabButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.setTab(btn));
      btn.addEventListener('keydown', (event) => this.handleTabKeydown(event, btn));
    });
  }

  setView(view) {
    if (!view) return;
    this.setAttribute('data-pic-view', view);
    this.buttons.forEach((btn) => {
      const isActive = btn.getAttribute('data-pic-toggle') === view;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  setTab(targetBtn) {
    if (!targetBtn) return;
    const targetPanelId = targetBtn.getAttribute('aria-controls');

    this.tabButtons.forEach((btn) => {
      const isActive = btn === targetBtn;
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.tabIndex = isActive ? 0 : -1;
    });

    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.id !== targetPanelId;
    });
  }

  handleTabKeydown(event, currentBtn) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const currentIndex = this.tabButtons.indexOf(currentBtn);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % this.tabButtons.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = this.tabButtons.length - 1;
    }

    event.preventDefault();
    const nextBtn = this.tabButtons[nextIndex];
    nextBtn.focus();
    this.setTab(nextBtn);
  }
}

customElements.define('product-info-card', ProductInfoCard);
